import { gql } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { omit } from 'ramda';
import { CategoryTreeFragment_categoryTree as NonRecursiveCategoryTree } from '../generated/types/CategoryTreeFragment';
import { titleize } from '../lib/titleize';
import { useUrlParams } from './useUrlParams';
import { usePLPParams } from './usePLPParams';

// graphql doesn't support recursive queries, making the interface recursive here, this makes it easier to work with
interface CategoryTree extends Omit<NonRecursiveCategoryTree, 'children'> {
  children?: (CategoryTree | null)[] | null;
}

export const CATEGORY_TREE_FRAGMENT = gql`
  fragment CategoryTreeFields on CategoryTree {
    name
    slug
    totalItems: total_items
  }
  fragment CategoryTreeFragment on CollectionResult {
    categoryTree: category_tree {
      ...CategoryTreeFields
      children {
        ...CategoryTreeFields
        children {
          ...CategoryTreeFields
          children {
            ...CategoryTreeFields
            children {
              ...CategoryTreeFields
            }
          }
        }
      }
    }
  }
`;

const DEFAULT_TREE: CategoryTree = {
  __typename: 'CategoryTree',
  name: null,
  slug: 'all',
  totalItems: 0,
  children: null
};

const LEVELS = ['root', 'vertical', 'category', 'subcategory'] as const;

const getSelectedNode = (
  node: CategoryTree,
  categories: { root?: undefined; vertical?: string; category?: string; subcategory?: string },
  level = 0
): CategoryTree => {
  const selectedChild = node.children?.find(child => child?.slug === categories[LEVELS[level + 1]]);
  return selectedChild ? getSelectedNode(selectedChild, categories, level + 1) : node;
};

const getActiveNode = (
  node: CategoryTree,
  categories: { root?: undefined; vertical?: string; category?: string; subcategory?: string },
  hasAttributes: boolean,
  level = 0
): CategoryTree => {
  const activeChild = node.children?.find(child => child?.slug === categories[LEVELS[level + 1]]);
  return activeChild &&
    ((activeChild.children || []).length > 0 ||
      (LEVELS[level + 1] === 'subcategory' && hasAttributes))
    ? getActiveNode(activeChild, categories, hasAttributes, level + 1)
    : node;
};

const getParentNode = (tree: CategoryTree, node: CategoryTree): CategoryTree | null => {
  if (!tree.children) return null;
  if (tree.children.includes(node)) return tree;
  return tree.children.map(child => child && getParentNode(child, node)).find(Boolean) || null;
};

const getPathToNode = (tree: CategoryTree, node: CategoryTree): CategoryTree[] => {
  const parent = getParentNode(tree, node);
  return parent ? [...getPathToNode(tree, parent), node] : [node];
};

const getNodeParams = (tree: CategoryTree, node: CategoryTree) => {
  const path = getPathToNode(tree, node);
  return LEVELS.reduce(
    (result, type, index) => (type === 'root' ? result : { ...result, [type]: path[index]?.slug }),
    {}
  );
};

const CATEGORY_NODE_HREF_OMITTED_KEYS = ['attributes', 'page'];

export const useCategoryTree = (categoryTree: CategoryTree | null, hasAttributes = false) => {
  const tree = useMemo(() => (categoryTree ? { ...categoryTree, name: null } : DEFAULT_TREE), [
    categoryTree
  ]);
  const { params: urlParams, getHrefFor } = useUrlParams();
  const {
    params: { vertical, category, subcategory }
  } = usePLPParams();

  const categories = useMemo(() => ({ vertical, category, subcategory }), [
    vertical,
    category,
    subcategory
  ]);

  const selectedCategoryNode = useMemo(() => getSelectedNode(tree, categories), [tree, categories]);

  const activeCategoryNode = useMemo(() => getActiveNode(tree, categories, hasAttributes), [
    tree,
    categories,
    hasAttributes
  ]);

  const parentCategoryNode = useMemo(() => getParentNode(tree, activeCategoryNode), [
    tree,
    activeCategoryNode
  ]);

  const parentCategoryName = useMemo(() => titleize(parentCategoryNode?.name || 'All Results'), [
    parentCategoryNode
  ]);

  const getCategoryNodeHref = useCallback(
    (node: CategoryTree) => {
      try {
        return getHrefFor({
          ...omit([...LEVELS.slice(1), ...CATEGORY_NODE_HREF_OMITTED_KEYS], urlParams),
          ...getNodeParams(tree, node)
        });
      } catch {
        return null;
      }
    },
    [tree, urlParams, getHrefFor]
  );

  const isTopLevel = useMemo(
    () => parentCategoryNode == null || getCategoryNodeHref(parentCategoryNode) == null,
    [parentCategoryNode, getCategoryNodeHref]
  );

  return {
    selectedCategoryNode,
    activeCategoryNode,
    parentCategoryNode,
    parentCategoryName,
    isTopLevel,
    getCategoryNodeHref
  };
};
